import { ReactElement } from "react"
import { Control } from "react-hook-form"

import ValidatedTextField from "../../inputs/validated/validatedTextField"

interface ExportMpanInputProps {
    registerName: string
    control: Control<any>
    applicationMpan: string
    disabled?: boolean
}

/**
 * Simple Export MPAN input component with minimal validation.
 * Only validates that the first two digits match the application MPAN's first two digits.
 */
const ExportMpanInput = ({
    registerName,
    control,
    applicationMpan,
    disabled,
}: ExportMpanInputProps): ReactElement => (
    <ValidatedTextField
        registerName={registerName}
        control={control}
        disabled={disabled}
        required
        label="Export MPAN"
        placeholder="Enter Export MPAN..."
        customRules={{
            validate: (value: string) => {
                // Note: required validation is handled by the ValidatedTextField component
                if (!value || !value.trim()) {
                    return true // Let the required rule handle this
                }

                // Validate length (13 digits for UK MPAN)
                if (value.length !== 13) {
                    return "Export MPAN must be 13 characters long"
                }

                // Validate that it's numeric
                if (
                    Number.isNaN(Number(value)) ||
                    !Number.isInteger(Number(value))
                ) {
                    return "Export MPAN must be numeric"
                }

                // Validate first two digits match application MPAN
                const exportMpanPrefix = value.substring(0, 2)
                const applicationMpanPrefix = applicationMpan.substring(0, 2)

                if (exportMpanPrefix !== applicationMpanPrefix) {
                    return `Export MPAN must start with the same two digits (${applicationMpanPrefix}) as the application MPAN`
                }

                return true
            },
        }}
        tooltip="Enter the Export MPAN. It must start with the same two digits as the application MPAN to match the licence area."
    />
)

ExportMpanInput.defaultProps = {
    disabled: false,
}

export default ExportMpanInput
